import React, { createContext, useContext, useState, useEffect } from "react";
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  sendEmailVerification,
  reload
} from "firebase/auth";
import { auth, db } from "../configs/firebase";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { generateInternID } from "../utils/generateUserID";

const AuthContext = createContext();
export const AuthData = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Set auth persistence on component mount
  useEffect(() => {
    const setupAuth = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);
        console.log('✅ Auth persistence set to LOCAL');
        setInitialized(true);
      } catch (error) {
        console.error('❌ Error setting auth persistence:', error);
        setInitialized(true); // Still allow the app to continue
      }
    };
    
    setupAuth();
  }, []);

  // Monitor auth state changes
  useEffect(() => {
    if (!initialized) return;

    console.log('🔧 Setting up auth state listener...');
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔄 Auth state changed:', {
        hasUser: !!firebaseUser,
        uid: firebaseUser?.uid,
        email: firebaseUser?.email,
        emailVerified: firebaseUser?.emailVerified
      });

      if (firebaseUser) {
        try {
          setLoading(true);
          
          // Check if this is a student by looking in the users collection
          console.log('📋 Fetching user profile from Firestore...');
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            console.log('✅ Student profile found:', {
              hasProfile: true,
              internID: userData.internID,
              firstName: userData.firstName,
              lastName: userData.lastName,
              emailVerified: firebaseUser.emailVerified
            });
            
            setUser({
              userd: firebaseUser,
              isAuthenticated: true,
              profile: userData,
              emailVerified: firebaseUser.emailVerified
            });
          } else {
              console.log('❌ No user profile found in Firestore');
              setUser({
                userd: firebaseUser,
                isAuthenticated: true,
                profile: null,
                emailVerified: firebaseUser.emailVerified
            });
          }
        } catch (error) {
          console.error('❌ Error fetching user profile:', error);
          setUser({
            userd: firebaseUser,
            isAuthenticated: true,
            profile: null,
            emailVerified: firebaseUser.emailVerified
          });
        }
      } else {
        setUser(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, [initialized]);

  // Logout function
  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      console.log('✅ User logged out successfully');
    } catch (error) {
      console.error('❌ Logout error:', error);
      throw error;
    }
  };

  // Login function
  const login = async (email, password) => {
    try {
      setLoading(true);
      
      const cleanEmail = String(email || '').trim().toLowerCase();
      const cleanPassword = String(password || '').trim();

      console.log('🔑 Student login attempt:', { cleanEmail });

      // Validation
      if (!cleanEmail || !cleanPassword) {
        throw new Error('Email and password are required');
      }

      if (!cleanEmail.includes('@')) {
        throw new Error('Invalid email format');
      }

      // Attempt Firebase login
      const userCredential = await signInWithEmailAndPassword(auth, cleanEmail, cleanPassword);
      const firebaseUser = userCredential.user;

      // Check if user exists in students collection
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      
      if (!userDoc.exists()) {
        // Check legacy collection
        const legacyDoc = await getDoc(doc(db, 'Interns', firebaseUser.uid));
        if (!legacyDoc.exists()) {
          await signOut(auth);
          throw new Error('Student account not found. Please use the correct login page.');
        }
      }
      
      // Check email verification status
      if (!firebaseUser.emailVerified) {
        console.log('⚠️ Email not verified for user:', firebaseUser.email);
        // Don't sign out, just inform about verification status
        // The UI can handle showing verification prompts based on user.emailVerified
      }
      
      // Return the user - the auth state listener will handle setting the user state
      return userCredential.user;
      
    } catch (error) {
      console.error('❌ Login error:', {
        code: error.code,
        message: error.message
      });
      
      setLoading(false);
      
      // Provide user-friendly error messages
      if (error.code === 'auth/user-not-found') {
        throw new Error('No account found with this email address');
      } else if (error.code === 'auth/wrong-password') {
        throw new Error('Incorrect password');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Invalid email address');
      } else if (error.code === 'auth/user-disabled') {
        throw new Error('This account has been disabled');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many failed login attempts. Please try again later');
      } else {
        throw error;
      }
    }
  };

  // Enhanced register function with email verification
  const register = async (userData) => {
    try {
      setLoading(true);
      console.log('📝 Student registration attempt:', {
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName
      });

      const { email, password, firstName, lastName, ...otherData } = userData;

      // Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      console.log('✅ Firebase user created:', firebaseUser.uid);

      // Generate intern ID
      const internID = await generateInternID(firstName);
      
      // Update Firebase profile
      const displayName = `${firstName} ${lastName}`;
      await updateProfile(firebaseUser, { displayName });

      // Create user document in Firestore
      const userDocData = {
        email: firebaseUser.email,
        firstName,
        lastName,
        internID,
        emailVerified: false, // Track verification status in Firestore too
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...otherData
      };

      await setDoc(doc(db, 'users', firebaseUser.uid), userDocData);
      
      console.log('✅ User profile created in Firestore with internID:', internID);

      // Send email verification
      try {
        await sendEmailVerification(firebaseUser, {
          url: `${window.location.origin}/profile`, // Redirect URL after verification
          handleCodeInApp: false
        });
        console.log('✅ Email verification sent to:', firebaseUser.email);
      } catch (verificationError) {
        console.error('❌ Error sending email verification:', verificationError);
        // Don't throw error here - account creation was successful
        // User can still use the app and request verification later
      }

      // Return the user - the auth state listener will handle setting the user state
      return firebaseUser;
      
    } catch (error) {
      console.error('❌ Registration error:', {
        code: error.code,
        message: error.message
      });
      
      setLoading(false);
      
      // Provide user-friendly error messages
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('An account with this email already exists');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('Password is too weak. Please use at least 6 characters.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Invalid email address');
      } else {
        throw error;
      }
    }
  };

  // Send email verification function
  const sendVerificationEmail = async () => {
    try {
      if (!auth.currentUser) {
        throw new Error('No user logged in');
      }

      await sendEmailVerification(auth.currentUser, {
        url: `${window.location.origin}/dashboard`,
        handleCodeInApp: false
      });

      console.log('✅ Verification email sent successfully');
      return true;
    } catch (error) {
      console.error('❌ Error sending verification email:', error);
      throw error;
    }
  };

  // Check and refresh email verification status
  const checkEmailVerification = async () => {
    try {
      if (!auth.currentUser) {
        throw new Error('No user logged in');
      }

      // Reload user to get fresh email verification status
      await reload(auth.currentUser);
      
      const verified = auth.currentUser.emailVerified;
      console.log('📧 Email verification status:', verified);

      // Update Firestore if verification status changed
      if (verified && user?.profile && !user.profile.emailVerified) {
        try {
          await updateDoc(doc(db, 'users', auth.currentUser.uid), {
            emailVerified: true,
            updatedAt: serverTimestamp()
          });
          console.log('✅ Updated email verification status in Firestore');
        } catch (firestoreError) {
          console.error('❌ Error updating Firestore:', firestoreError);
        }
      }

      return verified;
    } catch (error) {
      console.error('❌ Error checking email verification:', error);
      throw error;
    }
  };

  // Update user profile
  const updateUserProfile = async (uid, updateData) => {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
      
      console.log('✅ User profile updated successfully');
      return true;
    } catch (error) {
      console.error('❌ Profile update error:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    
    // Authentication methods
    register,
    login,
    logout,
    
    // Email verification methods
    sendVerificationEmail,
    checkEmailVerification,
    
    // Utility methods
    updateUserProfile,
    
    // User helpers
    isAuthenticated: () => !!user?.isAuthenticated,
    isEmailVerified: () => user?.emailVerified || false,
    hasProfile: () => !!user?.profile,
    getUserProfile: () => user?.profile || null
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};